import type * as React from "react";
import { cn } from "@/lib/cn";

/**
 * Single-select radio control with optional inline label.
 * Ported from the Wanted Design System bundle (components/forms/Radio.jsx).
 * Tellvue additions: `description`, and `variant="row"`, a full-width 48px+ card row used on
 * the customer page (selected = primary border + tint + filled dot, never color alone).
 */
export interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  checked?: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** @default "plain" */
  variant?: "plain" | "row";
}

export function Radio({
  checked = false,
  disabled = false,
  label,
  description,
  variant = "plain",
  onChange,
  style,
  className,
  ...rest
}: RadioProps) {
  const row = variant === "row";
  const rowLook: React.CSSProperties = row
    ? {
        display: "flex",
        width: "100%",
        minHeight: 48,
        padding: "12px 16px",
        borderRadius: "var(--radius-md)",
        boxSizing: "border-box",
        background: checked
          ? "color-mix(in srgb, var(--primary-normal) 8%, var(--background-normal-normal))"
          : "var(--background-normal-normal)",
        boxShadow: checked
          ? "inset 0 0 0 1.5px var(--primary-normal)"
          : "inset 0 0 0 1px var(--line-normal-neutral)",
        transition:
          "background var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard)",
      }
    : { display: "inline-flex" };
  return (
    <label
      className={cn("wds-control", row && "wds-control-row", className)}
      style={{
        position: "relative",
        alignItems: description || row ? "flex-start" : "center",
        gap: row ? 12 : 8,
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "var(--font-sans)",
        opacity: disabled && !checked ? 0.6 : 1,
        ...rowLook,
        ...style,
      }}
    >
      <input
        type="radio"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={{ position: "absolute", opacity: 0, width: 1, height: 1, margin: 0 }}
        {...rest}
      />
      <span
        className="wds-control-box"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          flex: "none",
          marginTop: row || description ? 1 : 0,
          borderRadius: "var(--radius-full)",
          background: "var(--background-normal-normal)",
          boxShadow: checked
            ? `inset 0 0 0 6.5px ${disabled ? "var(--interaction-disable)" : "var(--primary-normal)"}`
            : `inset 0 0 0 1.5px ${disabled ? "var(--line-normal-alternative)" : "var(--line-normal-strong)"}`,
          transition: "box-shadow var(--duration-fast) var(--ease-standard)",
        }}
      />
      {(label || description) && (
        <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
          {label && (
            <span
              style={{
                fontSize: row ? "inherit" : "var(--font-size-body-2)",
                lineHeight: row ? "1.45" : "var(--line-height-body-2)",
                fontWeight: row ? 600 : 500,
                color: "var(--label-normal)",
              }}
            >
              {label}
            </span>
          )}
          {description && (
            <span
              style={{
                fontSize: row ? "inherit" : "var(--font-size-label-2)",
                lineHeight: row ? "1.6" : "var(--line-height-label-2)",
                fontWeight: 400,
                color: "var(--label-neutral)",
              }}
            >
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  );
}

export default Radio;
